import React, { useEffect, useState, useCallback } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import axios from "axios";
import { fmtUSD } from "../../utils/currency";
import CRMPipelineView from "./CRMPipelineView";
import Contact360Drawer from "./Contact360Drawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { id: "lead", label: "Lead", color: "bg-slate-400", border: "border-slate-200" },
  { id: "onboarding", label: "Onboarding", color: "bg-blue-400", border: "border-blue-200" },
  { id: "accredited", label: "Accredited", color: "bg-emerald-400", border: "border-emerald-200" },
  { id: "first_investment", label: "First Investment", color: "bg-amber-400", border: "border-amber-200" },
  { id: "active", label: "Active", color: "bg-green-500", border: "border-green-200" },
  { id: "vip", label: "VIP", color: "bg-purple-500", border: "border-purple-200" },
];

function derivePipelineStage(inv) {
  const override = inv.extra?.pipeline_stage;
  if (override && STAGES.some(s => s.id === override)) return override;
  const accred = inv.accreditation_status;
  const deals = inv.active_deals || 0;
  const dist = inv.total_distributed || 0;
  if (accred === "verified" && deals > 1 && dist > 0) return "vip";
  if (accred === "verified" && deals > 0) return "active";
  if (accred === "verified" && deals === 0) return "first_investment";
  if (accred === "verified") return "accredited";
  if (accred === "pending" || accred === "pending_review") return "onboarding";
  return "lead";
}

const TABLE_COLUMNS = [
  { key: "full_name", label: "Name", render: inv => (
    <div>
      <div className="text-xs font-semibold text-[#0A0A0A]">{inv.full_name || inv.entity_name || "—"}</div>
      <div className="text-[10px] text-[#888]">{inv.email}</div>
    </div>
  )},
  { key: "accreditation_status", label: "Accreditation", render: inv => (
    <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${
      inv.accreditation_status === "verified" ? "bg-emerald-100 text-emerald-700" :
      inv.accreditation_status === "pending" || inv.accreditation_status === "pending_review" ? "bg-amber-100 text-amber-600" :
      "bg-slate-100 text-slate-600"
    }`}>{inv.accreditation_status || "N/A"}</span>
  )},
  { key: "total_committed", label: "Committed", render: inv => <span className="text-xs text-[#b89d5a] font-semibold">{fmtUSD(inv.total_committed || 0)}</span> },
  { key: "active_deals", label: "Active", render: inv => <span className="text-xs">{inv.active_deals || 0}</span> },
  { key: "total_distributed", label: "Distributed", render: inv => <span className="text-xs text-emerald-600 font-semibold">{fmtUSD(inv.total_distributed || 0)}</span> },
];

export default function CRMInvestors({ onViewDeal, onEntityNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/investors`, { headers });
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStageChange = (userId, newStage) => {
    setData(prev => ({
      ...prev,
      investors: (prev?.investors || []).map(inv =>
        inv.user_id === userId ? { ...inv, extra: { ...(inv.extra || {}), pipeline_stage: newStage } } : inv
      )
    }));
  };

  const handleApproveAccreditation = async (userId) => {
    setApprovingId(userId);
    try {
      await axios.put(`${API}/admin/investor/${userId}/accreditation`, { status: "verified" }, { headers });
      setData(prev => ({
        ...prev,
        investors: (prev?.investors || []).map(inv =>
          inv.user_id === userId ? { ...inv, accreditation_status: "verified" } : inv
        )
      }));
    } catch (err) { console.error(err); alert("Failed to approve accreditation"); }
    finally { setApprovingId(null); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="crm-investors-loading">
      <Loader2 className="h-8 w-8 animate-spin text-emerald-700" />
    </div>
  );

  const investors = data?.investors || [];

  const renderCardContent = (inv) => (
    <div className="flex items-center gap-2 mt-2 flex-wrap">
      <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${
        inv.accreditation_status === "verified" ? "bg-emerald-50 text-emerald-700 border border-emerald-100" :
        "bg-amber-50 text-amber-600 border border-amber-100"
      }`}>{inv.accreditation_status || "N/A"}</span>
      {(inv.total_committed || 0) > 0 && (
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-100">
          {fmtUSD(inv.total_committed)}
        </span>
      )}
      {(inv.active_deals || 0) > 0 && (
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-50 text-green-700 border border-green-100">
          {inv.active_deals} active
        </span>
      )}
    </div>
  );

  const extraActions = (inv) => {
    if (inv.accreditation_status === "verified") return null;
    return (
      <div className="mt-2 pt-2 border-t border-[#f0ebe0]">
        <button
          onClick={(e) => { e.stopPropagation(); handleApproveAccreditation(inv.user_id); }}
          disabled={approvingId === inv.user_id}
          className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-700 text-white text-[9px] font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 w-full justify-center"
          data-testid={`crm-i-approve-${inv.user_id}`}>
          {approvingId === inv.user_id ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <ShieldCheck className="h-3 w-3" />
          )}
          Approve Accreditation
        </button>
      </div>
    );
  };

  return (
    <div data-testid="crm-investors">
      <CRMPipelineView
        title="Investors"
        subtitle="Accreditation pipeline, capital deployment, and investor engagement"
        stages={STAGES}
        items={investors}
        deriveStageFn={derivePipelineStage}
        renderCardContent={renderCardContent}
        tableColumns={TABLE_COLUMNS}
        onSelectUser={setSelectedUser}
        onStageChange={handleStageChange}
        entityType="investor"
        extraActions={extraActions}
        testIdPrefix="crm-i"
      />

      <Contact360Drawer open={!!selectedUser} onClose={() => setSelectedUser(null)}
        userId={selectedUser} userType="investor" onViewDeal={onViewDeal} onEntityNavigate={onEntityNavigate} />
    </div>
  );
}
